import { Observable } from 'rxjs';
import type { DocumentReference, Query } from 'firebase/firestore';
import { onSnapshot } from 'firebase/firestore';

/**
 * rxjs adapters replacing `@angular/fire/firestore`'s `collectionData`/`docData`.
 *
 * Wrap `onSnapshot` (which fires immediately with current data, then on every
 * change) in an `Observable`; the SDK's unsubscribe function is the teardown.
 * Semantics match `@angular/fire`: optional `idField` injects the document id
 * into each row, and `docData$` emits `undefined` for a non-existent document.
 */

export interface FirestoreDataOptions {
  /** When set, the document id is written onto each emitted object under this key. */
  idField?: string;
}

export function collectionData$<T>(
  query: Query<T>,
  options: FirestoreDataOptions = {},
): Observable<T[]> {
  return new Observable<T[]>((subscriber) =>
    onSnapshot(
      query,
      (snapshot) =>
        subscriber.next(
          snapshot.docs.map((doc) =>
            options.idField ? ({ ...doc.data(), [options.idField]: doc.id } as T) : doc.data(),
          ),
        ),
      (error) => subscriber.error(error),
    ),
  );
}

export function docData$<T>(
  ref: DocumentReference<T>,
  options: FirestoreDataOptions = {},
): Observable<T | undefined> {
  return new Observable<T | undefined>((subscriber) =>
    onSnapshot(
      ref,
      (snapshot) => {
        const data = snapshot.data();
        subscriber.next(
          data && options.idField ? ({ ...data, [options.idField]: snapshot.id } as T) : data,
        );
      },
      (error) => subscriber.error(error),
    ),
  );
}
